import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";
import * as XLSX from 'xlsx';
import { User } from "../../../types/User";
import { Job } from "../../Jobs/types";
import { jobFormats, userFormats } from "./types";
import { mapExcelData } from "./utils";
import UploadIcon from '@mui/icons-material/Upload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import React from 'react';

interface FileUploadStepProps {
    importType: 'jobs' | 'users';
    importFormat: string;
    fileName: string;
    onImportTypeChange: (type: 'jobs' | 'users') => void;
    onImportFormatChange: (format: string) => void;
    onPreviewDataChange: (data: (Job | User)[]) => void;
    onFileNameChange: (name: string) => void;
    onStepChange: (step: number) => void;
    onReset: () => void;
}

const FileUploadStep = ({
    importType,
    importFormat,
    fileName,
    onImportTypeChange,
    onImportFormatChange,
    onPreviewDataChange,
    onFileNameChange,
    onStepChange,
    onReset
}: FileUploadStepProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);

    const getCurrentFormat = () => {
        return importType === 'jobs'
            ? jobFormats[importFormat]
            : userFormats[importFormat];
    };

    const downloadTemplate = () => {
        try {
            const format = getCurrentFormat();

            // Create a workbook with a single sheet
            const workbook = XLSX.utils.book_new();

            // Create headers from the format columns
            const headers = Object.values(format.columns).map(col => col.name);

            // Create a worksheet with headers
            const worksheet = XLSX.utils.aoa_to_sheet([headers]);

            // Set column widths based on content length
            const colWidths = headers.map(header => ({
                wch: Math.min(Math.max(header.length * 1.2, 15), 50) // Min width 15, max 50
            }));
            worksheet['!cols'] = colWidths;

            // Add the worksheet to the workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

            // Generate and download the Excel file
            XLSX.writeFile(workbook, `${importType}_${importFormat}_template.xlsx`);

            enqueueSnackbar('Template downloaded successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Error generating template', { variant: 'error' });
            console.error('Error generating template:', error);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        onFileNameChange(file.name);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const format = getCurrentFormat();
                const mappedData = await mapExcelData(jsonData, format, importType);
                onPreviewDataChange(mappedData);
                onStepChange(1); // Automatically switch to preview after successful parsing
            } catch (error) {
                console.error(error);
                if (error instanceof Error)
                    setError(error.message);
                else
                    setError('An unexpected error occurred while reading the file');
                onReset();
                enqueueSnackbar('Error reading Excel file', { variant: 'error' });
            }
        };
        reader.readAsBinaryString(file);
    };

    return (
        <Stack gap={2}>
            <FormControl fullWidth>
                <InputLabel>Import Type</InputLabel>
                <Select
                    size="small"
                    value={importType}
                    label="Import Type"
                    sx={{ backgroundColor: 'background.paper' }}
                    onChange={(e) => onImportTypeChange(e.target.value as 'jobs' | 'users')}
                >
                    <MenuItem value="jobs">Jobs</MenuItem>
                    <MenuItem value="users">Users</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth>
                <InputLabel>Import Format</InputLabel>
                <Select
                    size="small"
                    value={importFormat}
                    label="Import Format"
                    sx={{ backgroundColor: 'background.paper' }}
                    onChange={(e) => onImportFormatChange(e.target.value)}
                >
                    {importType === 'jobs' ? (
                        Object.entries(jobFormats).map(([key, format], index) => (
                            <MenuItem key={index} value={key}>{format.name}</MenuItem>
                        ))
                    ) : (
                        Object.entries(userFormats).map(([key, format], index) => (
                            <MenuItem key={index} value={key}>{format.name}</MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>

            <Paper
                variant="outlined"
                sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                }}
            >
                <Stack gap={2}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 2,
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Expected Format
                        </Typography>
                        <Button
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            onClick={downloadTemplate}
                            sx={{
                                borderRadius: 1
                            }}
                        >
                            Download Template
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            maxHeight: 300,
                            overflow: 'auto',
                            display: 'grid',
                            gridTemplateColumns: '3fr 1fr',
                            gap: 2,
                            '& > *': {
                                py: 1,
                                px: 2
                            }
                        }}
                    >
                        {Object.entries(getCurrentFormat().columns).map(([key, col], index) => (
                            <React.Fragment key={key}>
                                <Box
                                    key={`${key}-name`}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? 'background.default' : 'background.paper',
                                        borderRadius: 1,
                                        fontWeight: 500
                                    }}
                                >
                                    {col.name}
                                </Box>
                                <Box
                                    key={`${key}-type`}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? 'background.default' : 'background.paper',
                                        borderRadius: 1,
                                        fontStyle: 'italic'
                                    }}
                                >
                                    ({col.type})
                                </Box>
                            </React.Fragment>
                        ))}
                    </Box>
                </Stack>
            </Paper>

            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}

            {!fileName && (
                <Box

                >
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        ref={fileInputRef}
                        style={{
                            display: 'none',
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={() => fileInputRef.current?.click()}
                        startIcon={<UploadIcon />}
                    >
                        Select Excel File
                    </Button>
                </Box>
            )}

            {fileName && (
                <>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'background.paper',
                            borderRadius: 2
                        }}
                    >
                        <DescriptionIcon color="primary" />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Selected File
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 'medium',
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                {fileName}
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: 16,
                                        color: 'success.main'
                                    }}
                                />
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (fileInputRef.current)
                                    fileInputRef.current.value = '';
                                onPreviewDataChange([]);
                                onFileNameChange('');
                            }}
                            sx={{
                                color: 'error.main',
                                '&:hover': {
                                    backgroundColor: 'error.light'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Paper>
                    <Stack direction="row" justifyContent="flex-end">
                        <Button variant="contained" onClick={() => onStepChange(1)}>
                            Next
                        </Button>
                    </Stack>
                </>
            )}

        </Stack>
    );
};

export default FileUploadStep; 